/**
 * WhatsApp export → date range → attachments.
 *
 * Deliberately self-contained. It reads the zip, lets Julian pick a window, and
 * then hands the result to the composer through the EXISTING attachment action
 * (addAttachmentFromFile). It adds no state to the session store and does not
 * touch sendMessage, so the queue / drain / retry behaviour is untouched by
 * construction — if this component fails, it fails inside itself and the
 * composer still sends an ordinary message.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSession } from "../store/session";
import {
  baseName,
  findChatEntry,
  readZipEntry,
  readZipIndex,
  ZipError,
  type ZipEntry,
} from "../lib/whatsapp/zip";
import { parseChat, type ParsedChat } from "../lib/whatsapp/chat";
import {
  buildDocument,
  buildMessageBlock,
  humanBytes,
  sliceChat,
  stamp,
  type MediaRef,
  type SliceOptions,
} from "../lib/whatsapp/slice";

/** Slider bounds, in days back from the last message in the export. */
const MIN_DAYS = 1;
const MAX_DAYS = 14;

interface Props {
  file: File;
  onClose: () => void;
}

const pad = (n: number) => String(n).padStart(2, "0");
function toLocalInput(d: Date): string {
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}
function shortDate(d: Date): string {
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}
function shortTime(d: Date): string {
  return d.toLocaleTimeString("en-GB", { hour: "numeric", minute: "2-digit" });
}

async function sha256Hex(input: string | Uint8Array): Promise<string> {
  const bytes = typeof input === "string" ? new TextEncoder().encode(input) : input;
  const digest = await crypto.subtle.digest("SHA-256", bytes as unknown as ArrayBuffer);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

const MIME_BY_EXT: Record<string, string> = {
  jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", webp: "image/webp",
  gif: "image/gif", heic: "image/heic", heif: "image/heif", bmp: "image/bmp",
};
function mimeFor(filename: string): string {
  const ext = filename.split(".").pop()?.toLowerCase() ?? "";
  return MIME_BY_EXT[ext] ?? "application/octet-stream";
}

export default function WhatsAppImportSheet({ file, onClose }: Props) {
  const addAttachment = useSession((s) => s.addAttachmentFromFile);
  const setToast = useSession((s) => s.setToast);

  const [phase, setPhase] = useState<"reading" | "ready" | "working" | "error">("reading");
  const [error, setError] = useState<string>("");
  const [entries, setEntries] = useState<ZipEntry[]>([]);
  const [parsed, setParsed] = useState<ParsedChat | null>(null);
  const [chatSha, setChatSha] = useState("");
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  const [days, setDays] = useState(7);
  const [fromStr, setFromStr] = useState("");
  const [toStr, setToStr] = useState("");
  const [customised, setCustomised] = useState(false);
  const [opts, setOpts] = useState<SliceOptions>({ includePhotos: true, includeVideos: true });

  const cancelled = useRef(false);
  useEffect(() => () => { cancelled.current = true; }, []);

  // Escape closes, except while files are being uploaded — bailing mid-upload
  // would leave half the photos attached with no way to tell which.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && phase !== "working") {
        e.stopPropagation();
        onClose();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, onClose]);

  /* ---------------- read the archive ---------------- */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const idx = await readZipIndex(file);
        const chatEntry = findChatEntry(idx);
        if (!chatEntry) {
          throw new ZipError(
            "No chat transcript inside this zip. Export the chat again from WhatsApp.",
            "no_chat_txt",
          );
        }
        // Inflate the transcript ONCE. Reading it twice (text + bytes for the
        // hash) doubled the work on a chat file that can run to tens of MB.
        const raw = await readZipEntry(file, chatEntry);
        const text = new TextDecoder("utf-8", { fatal: false }).decode(raw);
        const mediaNames = idx.filter((e) => !e.isDirectory).map((e) => baseName(e.name));
        const p = parseChat(text, mediaNames);
        if (p.messages.length === 0) {
          throw new ZipError(
            "Couldn't read any messages from this export.",
            "no_messages",
          );
        }
        const sha = await sha256Hex(raw);
        if (!alive) return;
        setEntries(idx);
        setParsed(p);
        setChatSha(sha);
        const anchor = p.messages[p.messages.length - 1]!.at;
        setFromStr(toLocalInput(new Date(anchor.getTime() - 7 * 86400000)));
        setToStr(toLocalInput(anchor));
        setPhase("ready");
      } catch (err: unknown) {
        if (!alive) return;
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        setPhase("error");
      }
    })();
    return () => { alive = false; };
  }, [file]);

  const sizeByName = useMemo(() => {
    const m = new Map<string, number>();
    for (const e of entries) if (!e.isDirectory) m.set(baseName(e.name), e.uncompressedSize);
    return m;
  }, [entries]);

  const anchor = parsed?.messages[parsed.messages.length - 1]?.at ?? null;

  const applyDays = useCallback(
    (n: number) => {
      if (!anchor) return;
      setDays(n);
      setCustomised(false);
      setFromStr(toLocalInput(new Date(anchor.getTime() - n * 86400000)));
      setToStr(toLocalInput(anchor));
    },
    [anchor],
  );

  const from = fromStr ? new Date(fromStr) : null;
  const to = toStr ? new Date(toStr) : null;
  const rangeValid = !!from && !!to && !Number.isNaN(+from) && !Number.isNaN(+to) && +to > +from;

  // Keyed on the date STRINGS, not the Date objects — those are rebuilt every
  // render, which made this filter re-run over the whole chat on every keystroke.
  const slice = useMemo(() => {
    if (!parsed || !rangeValid) return null;
    return sliceChat(
      parsed,
      new Date(fromStr),
      new Date(toStr),
      opts,
      (name) => sizeByName.get(name) ?? null,
    );
  }, [parsed, rangeValid, fromStr, toStr, opts, sizeByName]);

  const first = slice?.messages[0] ?? null;
  const last = slice?.messages[slice.messages.length - 1] ?? null;

  /* ---------------- confirm ---------------- */
  async function confirm() {
    if (!parsed || !slice || !rangeValid) return;
    if (slice.messages.length === 0) {
      setToast("No messages in that range — widen it.", "error");
      return;
    }
    setPhase("working");
    try {
      const total = slice.photosToAttach.length + 1;
      let done = 0;
      setProgress({ done, total });

      // A photo can still fail here — a HEIC the browser can't decode, a 413,
      // a dropped connection. addAttachmentFromFile swallows that and toasts,
      // but the toast sits UNDER this sheet, so the failure would be invisible
      // AND the transcript would still promise the agent a file that never
      // arrived. So confirm each one landed and correct the slice before the
      // document is written from it.
      const attached: MediaRef[] = [];
      const failed: Array<MediaRef & { reason: string }> = [];
      for (const photo of slice.photosToAttach) {
        if (cancelled.current) return;
        const entry = entries.find((e) => baseName(e.name) === photo.filename);
        if (!entry) {
          failed.push({ ...photo, reason: "not found in the archive" });
          continue;
        }
        const bytes = await readZipEntry(file, entry);
        const blobPart = bytes as unknown as BlobPart;
        const asFile = new File([blobPart], photo.filename, { type: mimeFor(photo.filename) });
        await addAttachment(asFile);
        const added = useSession.getState().attachments.at(-1);
        if (added?.status === "ready" && added.serverPath) {
          attached.push(photo);
        } else {
          failed.push({ ...photo, reason: `upload failed${added?.error ? `: ${added.error}` : ""}` });
        }
        done++;
        setProgress({ done, total });
      }

      // Describe what actually happened, not what was planned.
      const effective = {
        ...slice,
        photosToAttach: attached,
        photosSkipped: [...slice.photosSkipped, ...failed],
      };
      const block = buildMessageBlock(effective, opts);
      const blockSha = await sha256Hex(block);
      const label = parsed.senders[0] ?? file.name.replace(/\.zip$/i, "");
      const doc = buildDocument(
        {
          chatLabel: label,
          archiveName: file.name,
          archiveBytes: file.size,
          archiveModified: file.lastModified ? new Date(file.lastModified) : null,
          chatTxtSha256: chatSha,
          messageBlockSha256: blockSha,
          parsed,
          slice: effective,
          opts,
          generatedAt: new Date(),
        },
        block,
      );
      const safeLabel = label.replace(/[^\w.-]+/g, "-").slice(0, 40);
      const docName =
        `WhatsApp-${safeLabel}-${shortDate(slice.from).replace(/\s/g, "")}` +
        `-to-${shortDate(slice.to).replace(/\s/g, "")}-${slice.messages.length}msgs.md`;
      await addAttachment(new File([doc], docName, { type: "text/markdown" }));
      done++;
      setProgress({ done, total });

      const bits = [`${effective.messages.length} messages`];
      if (attached.length) bits.push(`${attached.length} photos`);
      if (failed.length) bits.push(`${failed.length} photo(s) FAILED`);
      else if (slice.photosSkipped.length) bits.push(`${slice.photosSkipped.length} left out`);
      setToast(
        `Attached ${bits.join(" · ")}. Add a note and send.`,
        failed.length ? "error" : "success",
      );
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      setPhase("error");
      setToast(`Couldn't attach: ${msg}`, "error");
    }
  }

  /* ---------------- render ---------------- */
  // Fill has to end at the CENTRE of the thumb, so it is measured against the
  // track minus one thumb width — a plain percentage drifts off the knob.
  const fillRatio = (days - MIN_DAYS) / (MAX_DAYS - MIN_DAYS);

  /**
   * Portalled to <body> for a correctness reason, not a cosmetic one: the
   * composer is a <form>, and pressing Enter inside a date field nested in it
   * would submit that form — i.e. fire off the message mid-configuration.
   * Outside the form, Enter does nothing. It also escapes the composer's
   * stacking context.
   */
  return createPortal(
    <div
      className="waSheet"
      role="dialog"
      aria-label="Choose a date range"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && phase !== "working") onClose();
      }}
    >
      <div className="waSheet__panel">
        <div className="waSheet__handle" />

        <div className="waSheet__head">
          <button
            type="button"
            className="waSheet__x"
            onClick={onClose}
            disabled={phase === "working"}
            aria-label="Cancel"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" width="16" height="16">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
          <div className="waSheet__title">Date range</div>
          <button
            type="button"
            className="waSheet__ok"
            onClick={() => void confirm()}
            disabled={phase !== "ready" || !slice || slice.messages.length === 0}
            aria-label="Confirm range"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </button>
        </div>

        <div className="waSheet__body">
          {phase === "reading" && (
            <div className="waSheet__state">
              <div className="waSheet__spinner" />
              <div className="waSheet__stateTitle">Reading the export</div>
              <div className="waSheet__stateSub">
                {file.name} · {humanBytes(file.size)}
                <br />
                Unpacked on this device — nothing has been uploaded.
              </div>
            </div>
          )}

          {phase === "error" && (
            <div className="waSheet__state">
              <div className="waSheet__stateTitle">Couldn't read that file</div>
              <div className="waSheet__stateSub">{error}</div>
              <button type="button" className="waSheet__retry" onClick={onClose}>Close</button>
            </div>
          )}

          {phase === "working" && (
            <div className="waSheet__state">
              <div className="waSheet__spinner" />
              <div className="waSheet__stateTitle">
                Attaching {progress ? `${progress.done} of ${progress.total}` : ""}
              </div>
              <div className="waSheet__stateSub">Photos are compressed and uploaded one at a time.</div>
            </div>
          )}

          {phase === "ready" && parsed && (
            <>
              <div className="waSheet__readout">
                <div className="waSheet__days">
                  {customised ? "Custom" : `${days} ${days === 1 ? "day" : "days"}`}
                </div>
                <div className="waSheet__dates">
                  {rangeValid ? `${shortDate(from!)} → ${shortDate(to!)}` : "End must be after start"}
                </div>
                <div className="waSheet__anchor">
                  {anchor
                    ? `counting back from the last message · ${shortDate(anchor)}, ${shortTime(anchor)}`
                    : ""}
                </div>
              </div>

              <div className="waSlider">
                <span
                  className="waSlider__fill"
                  style={{ width: `calc(17px + (100% - 44px) * ${fillRatio})` }}
                />
                <span className="waSlider__ticks" aria-hidden="true">
                  {Array.from({ length: MAX_DAYS }, (_, i) => (
                    <i key={i} className={!customised && i + 1 <= days ? "is-on" : ""} />
                  ))}
                </span>
                <input
                  type="range"
                  min={MIN_DAYS}
                  max={MAX_DAYS}
                  step={1}
                  value={days}
                  onChange={(e) => applyDays(Number(e.currentTarget.value))}
                  aria-label="Days back from the last message"
                />
              </div>
              <div className="waSlider__ends">
                <span>1 day</span>
                <span>{MAX_DAYS} days</span>
              </div>

              <div className="waSheet__label">Exact</div>
              <div className="waSheet__exact">
                <label className="waField">
                  <span className="waField__k">Start</span>
                  <input
                    type="datetime-local"
                    value={fromStr}
                    max={toStr || undefined}
                    onChange={(e) => { setFromStr(e.currentTarget.value); setCustomised(true); }}
                  />
                </label>
                <label className="waField">
                  <span className="waField__k">End</span>
                  <input
                    type="datetime-local"
                    value={toStr}
                    min={fromStr || undefined}
                    max={anchor ? toLocalInput(anchor) : undefined}
                    onChange={(e) => { setToStr(e.currentTarget.value); setCustomised(true); }}
                  />
                </label>
              </div>

              <div className="waCount">
                <span className="waCount__dot" />
                <span>
                  {slice ? (
                    <>
                      <b>{slice.messages.length.toLocaleString()}</b> messages
                      {opts.includePhotos && slice.photosToAttach.length > 0 && (
                        <> · <b>{slice.photosToAttach.length}</b> photos</>
                      )}
                      {opts.includeVideos && slice.videos.length > 0 && (
                        <> · <b>{slice.videos.length}</b> {slice.videos.length === 1 ? "video" : "videos"}</>
                      )}
                    </>
                  ) : (
                    "—"
                  )}
                </span>
              </div>

              {/* The boundary check. A wrong date order or a mis-set window shows
                  up here as obviously-wrong text before anything is sent. */}
              {first && last && (
                <>
                  <div className="waSheet__label">First and last message in this window</div>
                  <div className="waBounds">
                    <div className="waBounds__row">
                      <span className="waBounds__k">First</span>
                      <span className="waBounds__v">
                        <span className="waBounds__t">{stamp(first.at)}</span>
                        <span className="waBounds__m">
                          {first.sender ? `${first.sender}: ` : ""}
                          {first.text.trim() || (first.media ? `[${first.media.kind}]` : "—")}
                        </span>
                      </span>
                    </div>
                    <div className="waBounds__row">
                      <span className="waBounds__k">Last</span>
                      <span className="waBounds__v">
                        <span className="waBounds__t">{stamp(last.at)}</span>
                        <span className="waBounds__m">
                          {last.sender ? `${last.sender}: ` : ""}
                          {last.text.trim() || (last.media ? `[${last.media.kind}]` : "—")}
                        </span>
                      </span>
                    </div>
                  </div>
                </>
              )}

              {parsed.warnings.map((w) => (
                <div key={w} className="waWarn">{w}</div>
              ))}

              {slice && slice.photosSkipped.length > 0 && (
                <div className="waWarn">
                  {slice.photosSkipped.length} photo(s) in this range won't be sent
                  {" — "}
                  {slice.photosSkipped[0]!.reason}. Narrow the range to include them.
                </div>
              )}

              <div className="waSheet__label" style={{ marginTop: 18 }}>Include</div>
              <div className={`waOpt ${opts.includePhotos ? "" : "is-off"}`}>
                <div>
                  <div className="waOpt__t">Photos</div>
                  <div className="waOpt__s">With the caption they were sent with</div>
                </div>
                <button
                  type="button"
                  className={`waToggle ${opts.includePhotos ? "is-on" : ""}`}
                  onClick={() => setOpts((o) => ({ ...o, includePhotos: !o.includePhotos }))}
                  aria-pressed={opts.includePhotos}
                  aria-label="Include photos"
                >
                  <span className="waToggle__knob" />
                </button>
              </div>
              <div className={`waOpt ${opts.includeVideos ? "" : "is-off"}`}>
                <div>
                  <div className="waOpt__t">Videos</div>
                  <div className="waOpt__s">Noted where they appear — the file itself stays on your phone</div>
                </div>
                <button
                  type="button"
                  className={`waToggle ${opts.includeVideos ? "is-on" : ""}`}
                  onClick={() => setOpts((o) => ({ ...o, includeVideos: !o.includeVideos }))}
                  aria-pressed={opts.includeVideos}
                  aria-label="Include videos"
                >
                  <span className="waToggle__knob" />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
